import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { usePromptBank } from "@/lib/profile/usePromptBank";
import { MAX_PROMPTS, type ProfilePrompt } from "@/lib/profile/types";
import type { LiveAppId as AppId } from "@/lib/apps";

interface Props {
  app: AppId;
  value: ProfilePrompt[];
  onChange: (prompts: ProfilePrompt[]) => void;
  max?: number;
}

const PromptsPicker = ({ app, value, onChange, max = MAX_PROMPTS }: Props) => {
  const { prompts: bank, loading } = usePromptBank(app as any);
  const [pickerOpen, setPickerOpen] = useState(false);

  const usedKeys = useMemo(() => new Set(value.map((p) => p.key)), [value]);
  const available = useMemo(
    () => bank.filter((b) => !usedKeys.has(b.prompt_key)),
    [bank, usedKeys],
  );

  const addPrompt = (key: string, text: string) => {
    if (value.length >= max) return;
    onChange([...value, { key, text, answer: "" }]);
    setPickerOpen(false);
  };

  const updateAnswer = (key: string, answer: string) => {
    onChange(value.map((p) => (p.key === key ? { ...p, answer } : p)));
  };

  const remove = (key: string) => {
    onChange(value.filter((p) => p.key !== key));
  };

  return (
    <div className="space-y-4">
      {value.map((p) => (
        <div key={p.key} className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium text-foreground">{p.text}</p>
            <button
              type="button"
              onClick={() => remove(p.key)}
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Remove prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Textarea
            value={p.answer}
            onChange={(e) => updateAnswer(p.key, e.target.value)}
            placeholder="Your answer…"
            rows={2}
            maxLength={250}
            className="mt-2 resize-none"
          />
          <p className="mt-1 text-[10px] text-muted-foreground text-right">
            {p.answer.length}/250
          </p>
        </div>
      ))}

      {value.length < max && (
        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" className="w-full">
              <Plus className="h-4 w-4" />
              Add a prompt ({value.length}/{max})
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Pick a prompt</DialogTitle>
            </DialogHeader>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : available.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No more prompts available.
              </p>
            ) : (
              <div className="space-y-2">
                {available.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => addPrompt(b.prompt_key, b.prompt_text)}
                    className="w-full text-left rounded-lg border border-border px-4 py-3 text-sm hover:bg-secondary transition-colors"
                  >
                    {b.prompt_text}
                  </button>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PromptsPicker;
